import React, { useState, useEffect } from 'react';
import { loadUsers } from '../utils/storage';

const EditClientForm = ({ client, onSave, onCancel }) => {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const allUsers = await loadUsers();
        // Filtrar solo usuarios estándar
        const standardUsers = allUsers.filter(user => user.role === 'standard');
        setUsers(standardUsers);
      } catch (error) {
        console.error('Error al cargar usuarios:', error);
        setUsers([]);
      }
    };
    fetchUsers();
  }, []);

  const [formData, setFormData] = useState({
    name: client.name,
    specialty: client.specialty || '',
    cell: client.cell || '',
    email: client.email || '',
    country: client.country || '',
    googleAdsLink: client.googleAdsLink || '',
    googleMapsLink: client.googleMapsLink || '',
    facebookLink: client.facebookLink || '',
    metaAccountLink: client.metaAccountLink || '',
    salesExecutive: client.salesExecutive || ''
  });

  // Estados para manejar múltiples células y ejecutivos
  const [selectedCells, setSelectedCells] = useState(() => {
    if (client.cells && Array.isArray(client.cells)) {
      return client.cells;
    } else if (client.cell) {
      return client.cell.split(',').map(c => c.trim()).filter(c => c !== '');
    }
    return [];
  });

  const [selectedExecutives, setSelectedExecutives] = useState(() => {
    if (client.salesExecutives && Array.isArray(client.salesExecutives)) {
      return client.salesExecutives;
    } else if (client.salesExecutive) {
      return client.salesExecutive.split(',').map(e => e.trim()).filter(e => e !== '');
    }
    return [];
  });

  // Lista de células disponibles
  const availableCells = [
    'C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA',
    'C1MA', 'C2MA', 'C3MA', 'C4MA',
    'G1M1', 'G1M2', 'G1M3', 'G1M4',
    'G2M1', 'G2M2', 'G2M3', 'G2M4',
    'G3M1', 'G3M2', 'G3M3', 'G3M4',
    'G4M1', 'G4M2', 'G4M3', 'G4M4',
    'G5M1', 'G5M2', 'G5M3', 'G5M4',
    'G6M1', 'G6M2', 'G6M3', 'G6M4'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Preparar datos con múltiples células y ejecutivos
    const updatedData = {
      ...client,
      ...formData,
      cell: selectedCells.join(','),
      salesExecutive: selectedExecutives.join(','),
      // Guardar también como arrays para uso futuro en el frontend
      cells: selectedCells,
      salesExecutives: selectedExecutives
    };
    
    onSave(updatedData);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-bold text-[#0061A8] mb-4">Editar Cliente</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#63666A]">Nombre</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">Especialidad</label>
          <input
            type="text"
            value={formData.specialty}
            onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        {/* Sección de Múltiples Células */}
        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Células (seleccione múltiples)
          </label>
          <div className="mt-1 grid grid-cols-3 gap-2 max-h-40 overflow-y-auto border border-[#D1D3D4] rounded-md p-3">
            {availableCells.map(cell => (
              <label key={cell} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedCells.includes(cell)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedCells([...selectedCells, cell]);
                    } else {
                      setSelectedCells(selectedCells.filter(c => c !== cell));
                    }
                  }}
                  className="rounded border-[#D1D3D4] text-[#0061A8] focus:ring-[#0061A8]"
                />
                <span className="text-sm text-[#63666A]">{cell}</span>
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-[#63666A]">
            Células seleccionadas: {selectedCells.length > 0 ? selectedCells.join(', ') : 'Ninguna'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">País</label>
          <input
            type="text"
            value={formData.country}
            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">Link de Google Ads</label>
          <input
            type="url"
            value={formData.googleAdsLink}
            onChange={(e) => setFormData({ ...formData, googleAdsLink: e.target.value })}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">Link de Google Maps</label>
          <input
            type="url"
            value={formData.googleMapsLink}
            onChange={(e) => setFormData({ ...formData, googleMapsLink: e.target.value })}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">Link de Facebook</label>
          <input
            type="url"
            value={formData.facebookLink}
            onChange={(e) => setFormData({ ...formData, facebookLink: e.target.value })}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">Link de Meta Business</label>
          <input
            type="url"
            value={formData.metaAccountLink}
            onChange={(e) => setFormData({ ...formData, metaAccountLink: e.target.value })}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        {/* Sección de Múltiples Ejecutivos de Ventas */}
        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Ejecutivos de Ventas (seleccione múltiples)
          </label>
          <div className="mt-1 grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-[#D1D3D4] rounded-md p-3">
            {users.map(user => (
              <label key={user.email} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedExecutives.includes(user.email)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedExecutives([...selectedExecutives, user.email]);
                    } else {
                      setSelectedExecutives(selectedExecutives.filter(email => email !== user.email));
                    }
                  }}
                  className="rounded border-[#D1D3D4] text-[#0061A8] focus:ring-[#0061A8]"
                />
                <span className="text-sm text-[#63666A]">{user.firstName} {user.lastName}</span>
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-[#63666A]">
            Ejecutivos seleccionados: {selectedExecutives.length > 0 ? 
              selectedExecutives.map(email => {
                const user = users.find(u => u.email === email);
                return user ? `${user.firstName} ${user.lastName}` : email;
              }).join(', ') : 'Ninguno'}
          </p>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="bg-[#63666A] text-white py-2 px-4 rounded-md hover:bg-opacity-90"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="bg-[#0061A8] text-white py-2 px-4 rounded-md hover:bg-opacity-90"
          >
            Guardar Cambios
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditClientForm;
